import React from "react";
import { motion } from "framer-motion";
import {
  FaCode,
  FaHandshake,
  FaTasks,
  FaEnvelope,
  FaMapMarkerAlt,
  FaLinkedin,
  FaGithub,
} from "react-icons/fa";

const ease = [0.22, 1, 0.36, 1];

/** Hand-drawn teal brush stroke behind the portrait (original SVG, roughened with a turbulence filter). */
const BrushStroke = ({ className = "" }) => (
  <svg
    viewBox="0 0 400 420"
    className={className}
    aria-hidden="true"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <filter id="brush-rough" x="-10%" y="-10%" width="120%" height="120%">
        <feTurbulence type="fractalNoise" baseFrequency="0.012 0.12" numOctaves="2" seed="4" result="noise" />
        <feDisplacementMap in="SourceGraphic" in2="noise" scale="14" xChannelSelector="R" yChannelSelector="G" />
      </filter>
    </defs>
    <g filter="url(#brush-rough)" fill="#a65f3b">
      <path d="M58 300 C 90 210, 170 120, 300 52 C 330 38, 352 58, 336 82 C 270 170, 190 250, 120 352 C 100 380, 46 350, 58 300 Z" />
      <path d="M40 210 C 110 150, 210 100, 330 120 C 360 126, 362 160, 334 166 C 240 186, 150 220, 80 276 C 52 298, 14 240, 40 210 Z" opacity="0.92" />
      <path d="M150 380 C 200 300, 260 240, 350 210 C 378 202, 386 236, 362 250 C 300 290, 250 340, 210 400 C 190 428, 134 410, 150 380 Z" opacity="0.9" />
      <path d="M96 150 C 140 110, 190 80, 250 70 C 270 68, 274 90, 256 98 C 210 118, 170 146, 132 186 C 112 206, 78 170, 96 150 Z" opacity="0.85" />
    </g>
  </svg>
);

const Hero = () => {
  return (
    <section className="relative bg-cream px-3 pb-20 md:px-6">
      <div className="container mx-auto">
        {/* Cream hero panel */}
        <div className="relative overflow-hidden rounded-[28px] bg-cream-100 px-6 pt-10 md:px-12 md:pt-14">
          <div className="grid grid-cols-1 items-end gap-8 md:grid-cols-2 lg:grid-cols-12">
            {/* Left: headline + email + location */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="flex flex-col self-stretch md:col-start-1 md:row-start-1 lg:col-span-4 lg:pb-14"
            >
              <h2 className="font-heading text-4xl font-semibold leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-[3.25rem]">
                Business Developer
                <br />
                <span className="text-pink-600">&amp; Project Manager</span>
              </h2>
              <p className="mt-4 inline-flex w-fit items-center gap-2 rounded-full border border-olive-300 bg-cream px-3 py-1 text-sm font-medium text-olive-600">
                <FaCode aria-hidden="true" /> …and a software engineer who still builds
              </p>

              <a
                href="mailto:taimanizar45@gmail.com"
                className="mt-10 inline-flex w-fit items-center gap-2 border-b-2 border-coral-400 pb-1 text-sm font-medium text-coral-500 transition hover:text-coral-600 lg:mt-auto"
              >
                <FaEnvelope aria-hidden="true" />
                taimanizar45@gmail.com
              </a>

              <div className="mt-6 flex items-center gap-3 text-ink">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-mustard-100 text-mustard-600">
                  <FaMapMarkerAlt aria-hidden="true" />
                </span>
                <span className="font-heading text-sm font-semibold uppercase leading-tight tracking-wide">
                  Palestine - Nablus
                </span>
              </div>
            </motion.div>

            {/* Center: portrait on brush stroke */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease }}
              className="relative mx-auto w-full max-w-[380px] md:col-start-2 md:row-span-2 md:row-start-1 md:self-end lg:col-span-4 lg:col-start-5 lg:row-span-1"
            >
              <BrushStroke className="absolute -left-[12%] bottom-0 top-[2%] h-[98%] w-[124%]" />
              <img
                src="/taima.png"
                alt="Taima Hamadneh"
                width="470"
                height="531"
                className="relative z-10 mx-auto block w-full object-contain"
              />
            </motion.div>

            {/* Right: intro + social badge */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease }}
              className="flex flex-col self-stretch pb-10 md:col-start-1 md:row-start-2 lg:col-span-4 lg:col-start-9 lg:row-start-1 lg:pb-14 lg:pl-6"
            >
              <p className="relative z-10 max-w-xs text-base leading-relaxed text-ink-soft lg:pt-4">
                I bring in new business, then plan and deliver the IT projects that come from it — from the first conversation at a conference or client meeting to proposal, delivery and support. As a computer engineer, I know exactly how they get built.
              </p>

              <div className="mt-10 flex items-center gap-4 lg:mt-auto">
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.linkedin.com/in/taimahamadneh/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn"
                    className="grid h-14 w-14 place-items-center rounded-full border-2 border-ink/80 text-xl text-ink transition hover:border-pink-600 hover:text-pink-600"
                  >
                    <FaLinkedin aria-hidden="true" />
                  </a>
                  <a
                    href="https://github.com/TaimaHamadneh"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                    className="grid h-14 w-14 place-items-center rounded-full border-2 border-ink/80 text-xl text-ink transition hover:border-pink-600 hover:text-pink-600"
                  >
                    <FaGithub aria-hidden="true" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Soft decorative dots */}
          <div className="pointer-events-none absolute left-[36%] top-12 hidden h-14 w-14 rounded-full bg-olive-300/40 lg:block" />
          <div className="pointer-events-none absolute -bottom-10 -right-10 hidden h-32 w-32 rounded-full bg-coral-300/25 lg:block" />
        </div>

        {/* Feature cards — "service card" style with coloured icon circles */}
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {[
            {
              icon: <FaHandshake />,
              tone: "bg-pink-500 text-white",
              title: "Business Development",
              text: "Finding new clients and partnerships through conferences, networking and client meetings — then turning them into proposals and statements of work.",
            },
            {
              icon: <FaTasks />,
              tone: "bg-ink text-white",
              title: "Project Management",
              text: "Running IT and website projects end-to-end: requirements, user stories, scheduling, team coordination, delivery and post-delivery support.",
            },
            {
              icon: <FaCode />,
              tone: "bg-olive-500 text-white",
              title: "Software Development",
              text: "Building systems hands-on with Appian, C#/.NET, Python and React — from banking solutions to full-stack web apps and APIs.",
            },
          ].map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + i * 0.08, ease }}
              whileHover={{ scale: 1.05 }}
              className="flex items-start gap-4 rounded-2xl border border-cream-200 bg-white p-6 shadow-[0_12px_30px_-18px_rgba(41,37,31,0.25)]"
            >
              <span className={`grid h-12 w-12 shrink-0 place-items-center rounded-full text-lg ${card.tone}`}>
                {card.icon}
              </span>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{card.title}</h3>
                <p className="mt-1 text-gray-600">{card.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
